import { Dispatch } from 'redux';
import * as api from '../utils/api';
import { NavigateFunction } from 'react-router-dom';

import { notifyError, notifySuccess, processError } from '../utils/functions';
import { showOverlay } from './overlay';

interface TeamsState {
  list?: ITeam[];
  current?: ITeam;
}

export const actions = {
  CLEAR_TEAM: 'lms-ui/teams/CLEAR_USER',
  UPDATE: 'lms-ui/teams/UPDATE',
  GET_TEAMS: 'lms-ui/teams/GET_USERS',
  GET_TEAM: 'lms-ui/teams/GET_USER',
};

interface TeamsAction {
  type: typeof actions.GET_TEAMS | typeof actions.GET_TEAM | typeof actions.UPDATE;
  payload: ITeam | ITeam[];
}

type TeamsActionTypes = TeamsAction;

const initialState: TeamsState = {
  list: [],
  current: undefined,
};

export const update = (payload: TeamsState) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clearTeam = () => {
  return {
    type: actions.CLEAR_TEAM,
  };
};

export const getTeams = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getTeams();
    const list = response.data;
    dispatch({ type: actions.GET_TEAMS, payload: list });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const getTeam = (teamId: string) => async (dispatch: Dispatch) => {
  try {
    dispatch(clearTeam());
    const response = await api.getTeam(teamId);
    const current = response.data as ITeam;
    dispatch({ type: actions.GET_TEAM, payload: current });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const addTeam =
  (settings: ITeamSettings, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const formData = getSettingsFormData(settings);
      const response = await api.addTeam(formData);
      const current = response.data as ITeam;
      navigate(`/admin/teams/${current._id}`);
      dispatch(update({ current }));
      notifySuccess('Team added');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

const getSettingsFormData = (settings: ITeamSettings) => {
  const formData = new FormData();
  const props = [
    'name',
    'isUnlocked',
    'enableTimeRelease',
    'ignoreExpiration',
    'expirationOverride',
    'logo',
    'signupCodes',
    'useWhiteFilter',
    'parent',
    'isMarmonTeam',
  ];
  props.forEach((p) => {
    const value = (settings as any)[p];
    if (value) {
      switch (p) {
        case 'signupCodes':
          value?.forEach((v: ISignupCode, i: number) => {
            Object.keys(v).forEach((k) => {
              if (k !== '_id' || !v.isNew) {
                formData.append(`${p}[${i}][${k}]`, v[k]);
              }
            });
          });
          break;
        case 'logo':
          formData.append(p, value);
          break;
        case 'expirationOverride':
          let data = value;
          // If value doesn't contain a Z, then it is in
          // the datetime-local format, so wrap in a Date
          // to convert to ISO/UTC
          if (data && data.includes && !data.includes('Z')) {
            data = new Date(data);
          }

          formData.append(p, data.toString());
          break;
        default:
          formData.append(p, value.toString());
          break;
      }
    }
  });

  return formData;
};

export const updateTeamSettings =
  (teamId: string, settings: ITeamSettings) => async (dispatch: Dispatch) => {
    try {
      const formData = getSettingsFormData(settings);
      const response = await api.updateTeamSettings(teamId, formData);
      const current = response.data as ITeam;
      dispatch(update({ current }));
      return Promise.resolve();
    } catch (e) {
      notifyError(e, dispatch);
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const updateTeamCourseAssignments =
  (teamId: string, assigned: ICourse[]) => async (dispatch: Dispatch) => {
    try {
      const courseIds = assigned.map((c) => c._id as string);
      const response = await api.updateTeamCourseAssignments(teamId, { courseIds });
      const current = response.data as ITeam;
      dispatch(update({ current }));
      return Promise.resolve();
    } catch (e) {
      notifyError(e, dispatch);
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const updateTeamLearnerAssignments =
  (
    teamId: string,
    courses: ILearnerCourseAssignment[],
    unassignedUserIds: string[],
    assignedUserIds: string[],
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.updateTeamLearnerAssignments(teamId, {
        courses,
        unassignedUserIds,
        assignedUserIds,
      });
      const current = response.data as ITeam;
      dispatch(update({ current }));
      return Promise.resolve();
    } catch (e) {
      notifyError(e, dispatch);
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const updateTeamManagerAssignments =
  (teamId: string, unassignedUserIds: string[], assignedUserIds: string[]) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.updateTeamManagerAssignments(teamId, {
        unassignedUserIds,
        assignedUserIds,
      });
      const current = response.data as ITeam;
      dispatch(update({ current }));
      return Promise.resolve();
    } catch (e) {
      notifyError(e, dispatch);
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const bulkUpdateNotification =
  (teamId: string, users: IUser[]) => async (dispatch: Dispatch) => {
    try {
      const response = await api.bulkUpdateNotification(teamId, users);
      const current = response.data as ITeam;
      dispatch(update({ current }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateManagerLearnerAssignments =
  (teamId: string, userId: string, learnerAssignments: IManagerLearnerAssignment[]) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.updateManagerLearnerAssignments(
        teamId,
        userId,
        learnerAssignments,
      );
      const current = response.data as ITeam;
      dispatch(update({ current }));
    } catch (e) {
      notifyError(e, dispatch);
      throw e;
    }
  };

export const sendTeamWelcomeEmail =
  (teamId: string, includeAll: boolean, includeSubteams: boolean) => async (dispatch: Dispatch) => {
    try {
      await api.sendTeamWelcomeEmail(teamId, includeAll, includeSubteams);
      notifySuccess('Email sent!');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const deactivateTeam =
  ({ teamId }: { teamId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const results = await api.deactivateTeam({ teamId });
      dispatch(update({ current: results.data }));
      notifySuccess('Team updated');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const deleteTeam =
  ({ teamId, navigate }: { teamId: string; navigate: NavigateFunction }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      await api.deleteTeam({ teamId });
      navigate(`/admin/teams`);
      dispatch(update({ current: undefined }));
      dispatch(showOverlay());
      notifySuccess('Team deleted');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const resetTeamCourseProgress =
  ({ teamId }: { teamId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      await api.resetTeamCourseProgress(teamId);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export default function reducer(state = initialState, action: TeamsActionTypes): TeamsState {
  switch (action.type) {
    case actions.UPDATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case actions.GET_TEAMS: {
      return {
        ...state,
        list: action.payload as ITeam[],
      };
    }
    case actions.GET_TEAM: {
      return {
        ...state,
        current: action.payload as ITeam,
      };
    }
    case actions.CLEAR_TEAM: {
      return {
        ...state,
        current: undefined,
      };
    }
    default:
      return state;
  }
}
